import * as Sentry from "@sentry/vue";
import axios from "axios";
import qs from "qs";
import type { ZodType } from "zod";

import type { ApiError } from "@/types/api";
import { camelToSnakeKeys } from "@/utils/string";

import type { Resource, ResourceDiff } from "./ResourceService";

axios.defaults.paramsSerializer = (params): string => {
    return qs.stringify(params, { arrayFormat: "brackets" });
};

export function axiosHandler(apiErrorCallback: (err: ApiError) => void) {
    return (err: unknown): void => {
        if (axios.isAxiosError(err) && err.response !== undefined) {
            apiErrorCallback(err.response.data);
        } else {
            throw err;
        }
    };
}

export type ErrorType = "not_found";

export abstract class ApiService {
    protected readonly url: string = import.meta.env.VITE_API_BASE_URL;
    protected readonly apiVersion: number = 1;

    protected get apiUrl(): string {
        return this.url.replace(/\{VERSION\}/, this.apiVersion.toString());
    }

    protected async get<T>(path: string, data?: Record<string, unknown>): Promise<T> {
        const headers = this.getCommonHeaders();
        const response = await axios.get<T>(`${this.apiUrl}/${path}`, { params: camelToSnakeKeys(data), headers });
        return response.data;
    }

    protected async post<T>(path: string, data: Record<string, unknown> | FormData = {}): Promise<T> {
        const headers = this.getCommonHeaders();
        if (!(data instanceof FormData)) {
            data = camelToSnakeKeys(data);
        }
        const response = await axios.post<T>(`${this.apiUrl}/${path}`, data, { headers });
        return response.data;
    }

    protected async patch<T extends Resource>(path: string, data: ResourceDiff<T>): Promise<T> {
        const headers = this.getCommonHeaders();
        const response = await axios.patch<T>(`${this.apiUrl}/${path}`, camelToSnakeKeys(data), { headers });
        return response.data;
    }

    protected async put<T>(path: string, data: object): Promise<T> {
        const headers = this.getCommonHeaders();
        const response = await axios.put<T>(`${this.apiUrl}/${path}`, camelToSnakeKeys(data), { headers });
        return response.data;
    }

    protected async delete<T>(path: string): Promise<T> {
        const headers = this.getCommonHeaders();
        const response = await axios.delete<T>(`${this.apiUrl}/${path}`, { headers });
        return response.data;
    }

    protected async getBlob(path: string, data?: Record<string, unknown>): Promise<Blob> {
        const headers = this.getCommonHeaders();
        const response = await axios.get(`${this.apiUrl}/${path}`, {
            params: camelToSnakeKeys(data),
            headers,
            responseType: "blob",
        });
        return response.data;
    }

    private getCommonHeaders(): Record<string, string> {
        const token = localStorage.getItem("token");
        const tokenHeaders: Record<string, string> = token !== null ? { Authorization: `Bearer ${token}` } : {};
        return tokenHeaders;
    }

    attemptParse<T>(schema: ZodType<T>, data: unknown): T {
        const parsed = schema.safeParse(data);
        if (parsed.success) {
            return parsed.data;
        } else {
            Sentry.captureException(parsed.error);
            if (import.meta.env.DEV) {
                console.error("Failed to parse response with schema. Using raw data.", {
                    issues: parsed.error.issues,
                    data,
                });
            }
            return data as T;
        }
    }
}
